<template>
  <div
    v-intersect="{ once: false, threshold: 0.5 }"
    class="product-grid-wrapper"
    @intersect:enter="onShowProductCard"
  >
    <div
      ref="product"
      class="product-grid"
      :class="[
        `product-grid--${theme}`,
        {
          'product-grid--hovered': hover,
          'z-index__4': hover,
          'product-grid--bordered': bordered
        }
      ]"
      @mouseenter="showHover"
      @mouseleave="hideHover"
    >
      <div class="product-grid__container z-index__5">
        <div
          class="product-grid__top"
          :class="{
            'z-index__5': hover
          }"
        >
          <ProductCardAgeRestrictions v-if="productAgeRestrictions" />

          <template v-else>
            <ProductCardImage
              class="product-grid__image"
              :in-stock="inStock"
              :was-sold="!inStock && wasSold"
              :images="product.images"
              :product-name="productName"
              :index="index"
              :width="imgWidth"
              :height="imgHeight"
              :href="linkToProduct"
              @click="handleLinkClick(linkToProduct)"
            />

            <div class="product-grid__label">
              <ProductLabels
                :category="product.category"
                :condition="product.offer_main.condition"
                :label="product.label"
                :offer-label="product.offer_main.label"
                :delivery="product.offer_main.delivery"
              />
            </div>
          </template>
        </div>

        <div class="product-grid__bottom-wrapper">
          <div v-if="theme === 'list'" class="product-grid__label-list">
            <ProductLabels
              :category="product.category"
              :condition="product.offer_main.condition"
              :label="product.label"
              :offer-label="product.offer_main.label"
              :delivery="product.offer_main.delivery"
              relative
              small
            />
          </div>

          <!-- TOP -->
          <div class="product-grid__about">
            <a
              class="product-grid__title"
              :href="linkToProduct"
              :title="productName"
              @click.prevent="handleLinkClick(linkToProduct)"
            >
              <span>{{ productName }}</span>
            </a>

            <div v-if="hasReviews" class="product-grid__rating">
              <div
                v-if="+product.rating > 0"
                class="product-grid__rating-stars"
              >
                <CommonStars :rating="product.rating" size="small" />
              </div>
              <NuxtLink
                class="link product-grid__rating-link"
                :to="`${linkToProduct}#reviews`"
              >
                <span>{{
                  $t('product.reviews_count', {
                    count: product.reviews_count
                  })
                }}</span>
              </NuxtLink>
            </div>
          </div>

          <!-- BOTTOM -->
          <div
            class="product-grid__bottom"
            :class="{
              'z-index__5': hover,
              'product-grid__bottom--no-reviews': !hasReviews
            }"
          >
            <ProductCardOffer
              :metric-block="block"
              :offer="product.offer_main"
              :product="product"
              :hovered="hover"
              :theme="theme"
              :is-wishlist-page="isWishlistPage"
              :show-bonuses-pay="showBonusesPay"
              :in-stock="inStock"
              :was-sold="!inStock && wasSold"
              :hide-wishlist="productAgeRestrictions"
              :hide-add-to-cart="productAgeRestrictions"
              :auction="auction"
              @remove-from-wishlist="handleRemoveFromWishlist"
            />
          </div>
        </div>
      </div>

      <div
        v-if="!disableHover && hasAttrs"
        class="product-grid__hover"
      >
        <LazyMediaQueryProductCardAttrs
          hydrate-on-media-query="(min-width: 768px)"
          :product-uuid="product.uuid"
          :attrs="product.attrs_main"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { ProductCatalog } from '~/types/product'

type Props = {
  product: ProductCatalog
  auction?: boolean // Change price box view
  block?: string
  theme?: 'default' | 'list'
  disableHover?: boolean
  isWishlistPage?: boolean
  imgWidth?: string
  imgHeight?: string
  index?: number | null
  showBonusesPay?: boolean
  bordered?: boolean // Always has border (because of the design for catalog grid product card has border with some conditions)
}

type Emits = {
  (eventName: 'go-to-product', index: number): void
  (eventName: 'remove-from-wishlist', value: string): void
}

const props = withDefaults(defineProps<Props>(), {
  auction: false,
  block: '',
  disableHover: false,
  theme: 'default',
  isWishlistPage: false,
  imgWidth: '100%',
  imgHeight: '100%',
  index: null,
  showBonusesPay: false
})
const emit = defineEmits<Emits>()
const { $device } = useNuxtApp()
const commonMetricsStore = useCommonMetricsStore()
const authUserStore = useAuthUserStore()

const localePath = useLocalePath()

// Ref
const hover = shallowRef(false)

// Computed
const ageRestrictions = computed(() => {
  return authUserStore.ageRestrictions
})
const inStock = computed(() => {
  return props.product.offer_main.in_stock
})
const wasSold = computed(() => {
  return props.product.offer_main.was_sold
})
const hasReviews = computed(() => {
  return props.product.reviews_count > 0
})
const productName = computed(() => {
  return props.product.name
})
const hasAttrs = computed(() => {
  return Boolean(props.product.attrs_main?.length)
})
const linkToProduct = computed(() => {
  let ulr = `/product/${props.product.slug}/${props.product.uuid}`

  if (props.product.offer_main) {
    ulr += `/${props.product.offer_main.code}`
  }
  return localePath(ulr)
})

const productAgeRestrictions = computed(() => {
  return props.product.category.ar_d && (ageRestrictions.value || ageRestrictions.value === null)
})

// Methods
const showHover = () => {
  if (!props.disableHover && $device.isDesktop) {
    hover.value = true
  }
}
const hideHover = () => {
  if ($device.isDesktop) {
    hover.value = false
  }
}

const handleLinkClick = (url: string) => {
  navigateTo(url)

  commonMetricsStore.addMetricEvent({
    element: 'ms[product-card]',
    event_type: 'click',
    event_content: {
      block: props.block,
      offerCode: props.product.offer_main.code
    }
  })

  if (props.index !== undefined) {
    emit('go-to-product', props.index!)
  }
}

const onShowProductCard = () => {
  commonMetricsStore.addMetricEvent({
    element: 'ms[product-card]',
    event_type: 'view',
    event_content: {
      block: props.block,
      offerCode: props.product.offer_main.code
    }
  })
}

const handleRemoveFromWishlist = (code: string) => {
  emit('remove-from-wishlist', code)
}

</script>

<style lang="scss" scoped>
.product-grid-wrapper {
  &:hover {
    .product-grid {
      &__container {
        position: relative;
      }

      &__hover {
        display: block;

        @media only screen and (max-width: 768px) {
          display: none;
        }
      }
    }
  }

}
.product-grid {
  height: 100%;
  $root: &;
  position: relative;

  &--bordered {
    border: 1px solid $border-color;
    border-radius: 4px;
  }

  &__top {
    height: 18rem;

    @media only screen and (min-width: 360px) {
      height: 20rem;
    }
    @media only screen and (min-width: 400px) {
      height: 22rem;
    }
    @media only screen and (min-width: 768px) and (max-width: 1024px) {
      height: 24rem;
    }
    @media only screen and (min-width: 1024px) {
      height: 25.9rem;
    }
  }

  &__bottom-wrapper {
    display: flex;
    flex-direction: column;
    height: calc(100% - 18rem);

    @media (orientation: landscape) and (max-height: 320px) and (max-width: 570px) {
      height: initial;
      flex-grow: 1;
    }

    @media only screen and (min-width: 360px) {
      height: calc(100% - 20rem);
    }

    @media only screen and (min-width: 400px) {
      height: calc(100% - 22rem);
    }

    @media only screen and (min-width: 768px) and (max-width: 1024px) {
      height: calc(100% - 24rem);
    }
    @media only screen and (min-width: 1024px) {
      height: calc(100% - 25.9rem);
    }
  }

  &__label {
    position: absolute;
    top: 1.3rem;
    left: 1.3rem;
    display: flex;
    flex-direction: column;
    z-index: 1;
  }

  &__label-list {
    @media only screen and (min-width: 1024px) {
      display: none;
    }
  }

  &__hover {
    display: none;
    position: relative;
  }

  &__container {
    padding: 1.6rem;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;

    @media only screen and (max-width: 768px) {
      padding: 0.8rem;
    }
  }

  &__title {
    font-size: 1.4rem;
    color: $font-color;
    line-height: 2.1rem;
    text-decoration: none;
    display: block;
    display: -webkit-box;
    word-break: break-word;
    height: 4.2rem;
    overflow: hidden;
    text-overflow: ellipsis;
    line-clamp: 2;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;

    @media only screen and (max-width: 520px) {
      line-height: 1.8rem;
      height: 3.8rem;
    }

    &:hover {
      color: #454545;
    }

    &:visited {
      color: $link-visited;
    }
  }

  &__rating {
    margin-top: 0.5rem;
    display: flex;
    align-items: center;
    height: 2.2rem;

    @media only screen and (max-width: 768px) {
      margin-top: 0;
    }
  }

  &__rating-link {
    font-size: 1.2rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__rating-stars {
    margin-right: 0.6rem;
    padding: 0.5rem 0;
  }

  &__about {
    margin-top: 0.8rem;
    flex-shrink: 0;
    min-height: 6rem;

    @media only screen and (min-width: 1024px) {
      margin-top: 2.6rem;
      min-height: 7rem;
    }
  }

  &__bottom {
    padding-top: 0.8rem;

    @media only screen and (max-width: 768px) {
      padding-top: 0.4rem;
    }
  }

  &--list {
    @media only screen and (max-width: 1024px) {
      border-radius: 0;
      box-shadow: 0 0.5px 0 0.5px $border-color;

      #{$root} {
        &__container {
          flex-direction: row;
          padding: 1.6rem 1.6rem 3rem 1.6rem;
        }

        &__image {
          object-fit: contain;
        }

        &__label {
          display: none;
        }

        &__top {
          flex-shrink: 0;
          width: 16.5rem;
          height: 16.5rem;
          margin-right: 1.6rem;
        }

        &__bottom-wrapper {
          flex-grow: 1;
          height: initial;
          justify-content: flex-start;
        }

        &__bottom {
          margin-top: 0;
        }

        &__about {
          margin: 0;
        }

        &__title {
          font-size: 1.2rem;
          text-overflow: unset;
          line-clamp: unset;
          -webkit-line-clamp: unset;
          height: auto;
        }
      }
    }
  }

  &--hovered {
    #{$root}__hover {
      display: block;
    }
  }
}
</style>
